import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { roleService } from '../services/auth/RoleService';

/**
 * usePermissions Hook
 * Cung cấp các helper functions để kiểm tra quyền user
 * 
 * Usage:
 * const { hasPermission, canCreateLessons, isAdmin } = usePermissions();
 * 
 * if (canCreateLessons) {
 *   // Show create lesson button
 * }
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const permissions = useMemo<string[]>(() => {
    if (!user || !user.permissions) return [];
    if (Array.isArray(user.permissions)) {
      return user.permissions;
    }
    // Handle new Object structure (permissions.capabilities)
    // If capabilities is missing in object, return empty array
    return (user.permissions as any).capabilities || [];
  }, [user]);

  const role = user?.role;

  // Basic permission checks
  const hasPermission = (permissionId: string): boolean => {
    return roleService.hasPermission(permissions, permissionId);
  };

  const hasAnyPermission = (permissionIds: string[]): boolean => {
    return roleService.hasAnyPermission(permissions, permissionIds);
  };

  const hasAllPermissions = (permissionIds: string[]): boolean => {
    return roleService.hasAllPermissions(permissions, permissionIds);
  };

  const canAccessResource = (resource: string, action: string): boolean => {
    return roleService.canAccessResource(permissions, resource, action);
  };

  // Role checks
  const isStudent = role === 'student';
  const isTeacher = role === 'teacher';
  const isAdmin = role === 'admin';
  const isTeacherOrAdmin = isTeacher || isAdmin;

  // Specific permission checks (commonly used)
  const canViewLessons = hasPermission('lessons:read');
  const canCreateLessons = hasPermission('lessons:write');
  const canDeleteLessons = hasPermission('lessons:delete');

  const canViewQuizzes = hasPermission('quizzes:read');
  const canCreateQuizzes = hasPermission('quizzes:write');
  const canDeleteQuizzes = hasPermission('quizzes:delete');
  const canSubmitQuizzes = hasPermission('quizzes:submit');

  const canViewUsers = hasPermission('users:read');
  const canManageUsers = hasPermission('users:manage');
  const canDeleteUsers = hasPermission('users:delete');

  const canViewStudents = hasPermission('students:read');
  const canManageStudents = hasPermission('students:manage');

  const canApproveTeachers = hasPermission('teachers:approve');

  const canViewReports = hasPermission('reports:read');
  const canCreateReports = hasPermission('reports:write');

  const canManageSystem = hasPermission('system:manage');

  // Flashcard permissions (for teachers)
  const canCreateFlashcards = hasPermission('lessons:write') || isTeacherOrAdmin;
  const canManageFlashcards = isTeacherOrAdmin;

  return {
    // Raw permissions array
    permissions,
    role,

    // Basic checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessResource,

    // Role checks
    isStudent,
    isTeacher,
    isAdmin,
    isTeacherOrAdmin,

    // Lesson permissions
    canViewLessons,
    canCreateLessons,
    canDeleteLessons,

    // Quiz permissions
    canViewQuizzes,
    canCreateQuizzes,
    canDeleteQuizzes,
    canSubmitQuizzes,

    // User management permissions
    canViewUsers,
    canManageUsers,
    canDeleteUsers,

    // Student management permissions
    canViewStudents,
    canManageStudents,

    // Teacher management permissions
    canApproveTeachers,

    // Report permissions
    canViewReports,
    canCreateReports,

    // System management permissions
    canManageSystem,

    // Flashcard permissions
    canCreateFlashcards,
    canManageFlashcards,
  };
};

export default usePermissions;

